import { useTranslation } from 'react-i18next';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import QrCodeGenerator from '@/components/atoms/QrCodeGenerator/QrCodeGenerator';
import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useViber } from '@/hooks/useViber';

import styles from './ViberInfo.module.scss';

interface Props {
  deskId: string;
  integrationId: string;
}
const ViberInfo = ({ deskId, integrationId }: Props) => {
  const { t } = useTranslation();
  const { viberData, isLoading, error } = useViber(deskId, integrationId);
  const viberUri = `viber://pa?chatURI=${viberData?.uri}`;
  const image = `data:image/jpeg;base64,${viberData?.imagebase64}`;
  return (
    <div className={styles.container}>
      <div className={styles.title}>{t('integrations.viber_info.overview')}</div>
      <div className={styles.header}>
        <div className={styles.card}>
          {isLoading && <Skeleton className="h-16 w-16 rounded-full" />}
          {error && (
            <Avatar>
              <AvatarFallback>?</AvatarFallback>
            </Avatar>
          )}
          {!isLoading && !error && (
            <img src={viberData?.icon} alt="Viber Icon" referrerPolicy="no-referrer" />
          )}

          <div>
            {isLoading && (
              <>
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 w-[100px]" />
              </>
            )}
            {!isLoading && (
              <>
                <p className={styles.name}>
                  {viberData?.name || t('integrations.viber_info.account_name')}
                </p>
                <p className={styles.uri}>Uri: {viberData?.uri || '-'}</p>
              </>
            )}

            {!isLoading && (
              <CopyButton className="primary" disabled={error} data={viberUri} iconPosition="left">
                {t('integrations.viber_info.deep_link')}
              </CopyButton>
            )}
          </div>
        </div>
        {!isLoading && !error && (
          <div className={styles.qrcode}>
            <QrCodeGenerator url={viberUri} image={image} />
          </div>
        )}
      </div>
      <div className={styles.dataContainer}>
        <ReadOnlyField
          className="mb-3"
          isLoading={isLoading}
          label={t('integrations.viber_info.followers')}
          value={viberData?.subscribers_count}
        />
        <ReadOnlyField
          className="mb-3"
          isLoading={isLoading}
          label={t('integrations.viber_info.category')}
          value={viberData?.category}
        />
        <ReadOnlyField
          className="mb-3"
          isLoading={isLoading}
          label={t('integrations.viber_info.subcategory')}
          value={viberData?.subcategory}
        />
        <ReadOnlyField
          isLoading={isLoading}
          label={t('integrations.viber_info.country')}
          value={viberData?.country}
        />
      </div>
    </div>
  );
};

export default ViberInfo;
